import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowDown,
  ArrowRightLeft,
  ArrowUp,
  ArrowUpDown,
  Ban,
  Check,
  ChevronDown,
  History,
  Mail,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import {
  useClinicianClinics,
  useCurrentClinician,
  useDeregisterPatient,
  usePatient,
  usePatientList,
  useResendInvitationEmail,
  useRevokeClinicInvitation,
} from '@/hooks/queries';
import type { PatientsTableFilter } from '@/api/patients';
import { ClinicLabel, useIsInternalUser } from '@/components/ClinicLabel';
import { DataTable, type Column } from '@/components/DataTable';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from '@/components/ui/toaster';
import { useAuthStore } from '@/auth';
import { clinicianHomeHospitalId } from '@/api/clinicianHospital';
import type { ClinicResponse } from '@/api/hospital';
import { cn } from '@/lib/utils';
import type { PatientListRow } from '@/api/patients';
import {
  anyClinicHasExternalId,
  unionClinicCustomFields,
} from '@/lib/hospitalCustomFields';
import { ANALYTICS_EVENTS, track } from '@/lib/analytics';
import { useRetainPatientListFilters } from '@/hooks/usePatientListFilters';
import { NewPatientDialog } from './components/NewPatientDialog';
import { QuickAddPatientDialog } from './components/QuickAddPatientDialog';
import {
  canMovePatientBetweenClinics,
  MovePatientClinicDialog,
} from './components/MovePatientClinicDialog';


type SortKey = 'name' | 'email' | 'last_meal' | 'clinics' | 'logging_period';
type SortDir = 'asc' | 'desc';

type StatusFilter =
  | 'all'
  | 'active'
  | 'expired'
  | 'upcoming'
  | 'no_subscription'
  | 'pending_invitation';

const STATUS_LABELS: Record<StatusFilter, string> = {
  all: 'All',
  active: 'Active sub',
  expired: 'Expired sub',
  upcoming: 'Upcoming sub',
  no_subscription: 'No sub',
  pending_invitation: 'Pending invite',
};

const STATUS_OPTIONS: StatusFilter[] = [
  'all',
  'active',
  'upcoming',
  'expired',
  'no_subscription',
  'pending_invitation',
];

/**
 * Flag filter — mirrors v1's getFlagFilters (patientlist.tsx:27-34). Each
 * value maps to one or more server-filter fields in buildServerFilter().
 * 'all' means "no flag filter applied".
 */
type FlagFilter = 'all' | 'not_seen_1mo' | 'report_soon' | 'draft_report';
const FLAG_LABELS: Record<FlagFilter, string> = {
  all: 'All flags',
  not_seen_1mo: "Hasn't logged 1mo",
  report_soon: 'Report soon',
  draft_report: 'Draft report',
};
const FLAG_OPTIONS: FlagFilter[] = ['all', 'not_seen_1mo', 'report_soon', 'draft_report'];

/** Added-by filter. Three-state: anyone (default) / me / others. */
type AddedByFilter = 'all' | 'me' | 'others';
const ADDED_BY_LABELS: Record<AddedByFilter, string> = {
  all: 'Anyone',
  me: 'Added by me',
  others: 'Added by others',
};
const ADDED_BY_OPTIONS: AddedByFilter[] = ['all', 'me', 'others'];

/** Sentinel for the "hasn't logged in a month" option surfaced inside the
 *  combined "Patient status" dropdown (it otherwise lives on the `flag`
 *  filter as `not_seen_1mo`). */
const NOT_LOGGED_VALUE = '__not_logged_1mo';

/** Page-size choices for the limit dropdown. v1 went up to 500; capping
 *  at 200 here because larger pages are noticeably slower in V2's table
 *  render and rarely worth the wait — clinicians who want everything
 *  usually filter rather than page through 500+ rows. */
const PAGE_SIZE_OPTIONS = [25, 50, 100, 200] as const;
const DEFAULT_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

const compareNullable = (a: string | null | undefined, b: string | null | undefined): number => {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;
  return a.localeCompare(b);
};

/**
 * Build the server-side filter object that the patients-table endpoint
 * understands when JSON-serialized into the `filter` query param. Mirrors
 * v1's `filterObj` build (clinician/src/pages/patientlist.tsx:554-616).
 *
 * `active`, `upcoming`, `expired` map to the server's boolean
 * `has_*_subscription` fields directly. `no_subscription` and
 * `pending_invitation` aren't supported by the v1 filter shape — those are
 * applied client-side as a best-effort fallback (only filters the current
 * server page, so they're approximate when the patient list spans
 * pagination).
 */
/** Format a Date as YYYY-MM-DD (local time). The server's date-comparison
 *  filters work on ISO date strings, not timestamps. */
const isoDate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

const buildServerFilter = (args: {
  search: string;
  status: StatusFilter;
  flag: FlagFilter;
  addedBy: AddedByFilter;
  clinicIds: number[];
  clinicianId: number | undefined;
}): PatientsTableFilter => {
  const f: PatientsTableFilter = {};
  if (args.search.trim()) f.nameEmail = { contains: args.search.trim() };
  if (args.clinicIds.length > 0) f.clinic_ids = { array_contains_any: args.clinicIds };
  switch (args.status) {
    case 'active':
      f.has_active_subscription = true;
      break;
    case 'upcoming':
      f.has_upcoming_subscription = true;
      break;
    case 'expired':
      f.has_expired_subscription = true;
      break;
    // 'no_subscription' and 'pending_invitation' fall through — applied client-side below.
  }
  // Flag filters — mirror v1's switch at patientlist.tsx:556-573. The two
  // date-based filters compute their windows in local time so a clinician
  // in PT sees the same "1 month ago" as the server (which also lives in
  // local time per row). Acceptable drift on the day boundary.
  if (args.flag !== 'all') {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    switch (args.flag) {
      case 'not_seen_1mo': {
        const cutoff = new Date(today);
        cutoff.setMonth(cutoff.getMonth() - 1);
        f.last_meal_date = { le: isoDate(cutoff) };
        break;
      }
      case 'report_soon': {
        const lo = new Date(today);
        lo.setDate(lo.getDate() - 3);
        const hi = new Date(today);
        hi.setDate(hi.getDate() + 3);
        f.next_report_date = { ge: isoDate(lo), le: isoDate(hi) };
        break;
      }
      case 'draft_report':
        f.has_draft_report = true;
        break;
    }
  }
  // Added-by — server supports both eq and ne on registered_by_user_id.
  if (args.addedBy !== 'all' && args.clinicianId != null) {
    f.registered_by_user_id = args.addedBy === 'me'
      ? { eq: args.clinicianId }
      : { ne: args.clinicianId };
  }
  return f;
};

/**
 * Best-effort client-side filter for statuses the v1 filter object doesn't
 * support (`no_subscription`, `pending_invitation`). Applied to the current
 * server page; if the matches are on a later page they won't appear until
 * paginated to. Keeping this until the server's `filter` shape gains these
 * fields.
 */
const matchesClientSideStatus = (row: PatientListRow, status: StatusFilter): boolean => {
  switch (status) {
    case 'no_subscription':
      return (
        !row.has_active_subscription &&
        !row.has_expired_subscription &&
        !row.has_completed_subscription &&
        !row.has_upcoming_subscription
      );
    case 'pending_invitation':
      return row.invitation_status === 'pending';
    default:
      return true;
  }
};

/**
 * Single-select filter dropdown (Patient status / Report status / Added by).
 * Replaces the old chip rows: the trigger shows the label + current choice
 * (tinted when it's not the default "All"), and the Popover closes on outside
 * click for free. The first option is treated as the default/"clear" value.
 */
function FilterSelect<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: Array<{ value: T; label: string }>;
  onChange: (v: T) => void;
}) {
  const [open, setOpen] = useState(false);
  const current = options.find((o) => o.value === value) ?? options[0];
  const isDefault = value === options[0]?.value;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('h-9 gap-1.5', !isDefault && 'border-primary text-primary')}
        >
          <span className={cn('text-xs', isDefault ? 'text-muted-foreground' : 'text-primary/80')}>
            {label}:
          </span>
          <span className="text-xs font-medium">{current?.label}</span>
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-52 p-1">
        {options.map((o) => (
          <button
            key={o.value}
            type="button"
            onClick={() => {
              onChange(o.value);
              setOpen(false);
            }}
            className={cn(
              'flex w-full items-center justify-between gap-2 rounded px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted',
              o.value === value && 'font-medium text-primary',
            )}
          >
            <span className="truncate">{o.label}</span>
            {o.value === value && <Check className="h-3.5 w-3.5 shrink-0" />}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

const SortHeader = ({
  label,
  active,
  dir,
  onClick,
}: {
  label: string;
  active: boolean;
  dir: SortDir;
  onClick: () => void;
}) => {
  const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex items-center gap-1 text-left transition-colors hover:text-foreground',
        active ? 'text-foreground' : 'text-muted-foreground',
      )}
    >
      {label}
      <Icon className="h-3 w-3" />
    </button>
  );
};

const formatDateShort = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return iso;
  }
};

/** True if a date string represents a moment within the past N days. */
const withinDays = (iso: string | null | undefined, days: number): boolean => {
  if (!iso) return false;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return false;
  return Date.now() - d.getTime() <= days * 24 * 60 * 60 * 1000;
};

/**
 * Compact action column for the patient list — four small icons matching v1's
 * flagIcon pattern (clinician/src/pages/patientlist.tsx:480-541):
 *
 *   ⏱  History — red when patient was active in the last month
 *   ✉  Envelope — resend invitation email (active when not yet registered)
 *   🚫 Ban — revoke the pending invitation (active when status === 'pending')
 *   🗑  Trash — deregister patient from a clinic (active when in clinic)
 *
 * Inactive (greyed-out) icons are still clickable so the clinician can take
 * the action regardless of state — e.g. force-resend even after registration.
 */
const PatientFlagIcons = ({
  row,
  hospitalId,
  defaultClinicId,
  clinicianClinics,
  homeHospitalId,
}: {
  row: PatientListRow;
  hospitalId: number | null | undefined;
  /** First clinic the current clinician shares with the patient — used as
   *  the deregister target. Falls back to the patient's first clinic. */
  defaultClinicId?: number;
  clinicianClinics: ClinicResponse[];
  homeHospitalId?: number | null;
}) => {
  const { t } = useTranslation();
  const resend = useResendInvitationEmail(hospitalId);
  const revoke = useRevokeClinicInvitation();
  const deregister = useDeregisterPatient(hospitalId);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [moveOpen, setMoveOpen] = useState(false);

  const isUserInClinic =
    !row.invitation_status || row.invitation_status === 'accepted';
  const hasLoggedRecently = withinDays(row.last_meal_date, 31);
  const isPendingInvite = row.invitation_status === 'pending';
  // Resolve the clinic to deregister from — first one the row is in.
  const deregisterClinicId = defaultClinicId ?? row.clinics?.[0]?.id;

  const showMove = canMovePatientBetweenClinics({
    patientClinics: row.clinics,
    clinicianClinics,
    homeHospitalId,
  }) && isUserInClinic;

  const onResend: React.MouseEventHandler = async (e) => {
    e.stopPropagation();
    if (row.is_registered && isUserInClinic) {
      toast({ title: t('Patient is already registered') });
      return;
    }
    try {
      await resend.mutateAsync({ patientId: row.patient_id, invitationId: row.invitation_id });
      toast({
        title: t('Invitation email sent to {{name}}', {
          name: `${row.first_name ?? ''} ${row.last_name ?? ''}`.trim(),
        }),
      });
      track(ANALYTICS_EVENTS.PATIENT_RESEND_INVITE_CLICKED);
    } catch {
      toast({ title: t('Failed to send invitation email'), variant: 'destructive' });
    }
  };

  const onRevoke = async () => {
    if (!row.invitation_id) {
      toast({ title: t('No invitation to revoke') });
      setConfirmRevoke(false);
      return;
    }
    try {
      await revoke.mutateAsync(row.invitation_id);
      toast({ title: t('Invitation revoked') });
    } catch {
      toast({ title: t('Failed to revoke invitation'), variant: 'destructive' });
    } finally {
      setConfirmRevoke(false);
    }
  };

  const onDelete = async () => {
    if (!deregisterClinicId) {
      toast({ title: t('No clinic available to deregister from') });
      setConfirmDelete(false);
      return;
    }
    try {
      await deregister.mutateAsync({
        clinicId: deregisterClinicId,
        patientId: row.patient_id,
      });
      toast({ title: t('Patient removed from clinic') });
      track(ANALYTICS_EVENTS.PATIENT_DELETED);
    } catch {
      toast({ title: t('Failed to remove patient'), variant: 'destructive' });
    } finally {
      setConfirmDelete(false);
    }
  };

  // Click-stop wrappers so icon clicks don't bubble to the row's onClick
  // (which navigates to the patient page).
  const stop = (handler: (e: React.MouseEvent) => void) => (e: React.MouseEvent) => {
    e.stopPropagation();
    handler(e);
  };

  return (
    <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
      {/* Active-in-last-month indicator — read-only signal, not clickable. */}
      <button
        type="button"
        title={t('Red — patient was active in the last month')}
        className="cursor-default rounded p-1"
        tabIndex={-1}
      >
        <History
          className={cn(
            'h-4 w-4',
            hasLoggedRecently && isUserInClinic ? 'text-rose-600' : 'text-muted-foreground/40',
          )}
        />
      </button>

      {/* Resend invitation email. */}
      <button
        type="button"
        title={t('Resend invitation email')}
        onClick={onResend}
        disabled={resend.isPending}
        className="rounded p-1 transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Mail
          className={cn(
            'h-4 w-4',
            !row.is_registered || !isUserInClinic ? 'text-sky-600' : 'text-muted-foreground/40',
          )}
        />
      </button>

      {/* Revoke pending invitation. */}
      <button
        type="button"
        title={t('Revoke invitation')}
        onClick={stop(() => setConfirmRevoke(true))}
        disabled={revoke.isPending}
        className="rounded p-1 transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Ban
          className={cn(
            'h-4 w-4',
            isPendingInvite ? 'text-amber-600' : 'text-muted-foreground/40',
          )}
        />
      </button>

      {/* Move to another clinic in the same hospital (FOOD-651). */}
      {showMove && (
        <button
          type="button"
          title={t('Move patient to another clinic')}
          onClick={stop(() => setMoveOpen(true))}
          className="rounded p-1 transition-colors hover:bg-muted"
        >
          <ArrowRightLeft className="h-4 w-4 text-teal-700" />
        </button>
      )}

      {/* Delete from clinic. */}
      <button
        type="button"
        title={t('Remove patient from clinic')}
        onClick={stop(() => setConfirmDelete(true))}
        disabled={deregister.isPending}
        className="rounded p-1 transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
      >
        <Trash2
          className={cn(
            'h-4 w-4',
            isUserInClinic ? 'text-rose-600' : 'text-muted-foreground/40',
          )}
        />
      </button>

      <ConfirmDialog
        open={confirmRevoke}
        onOpenChange={setConfirmRevoke}
        title={t('Revoke invitation?')}
        description={t('Revoke the pending invitation for {{name}}?', {
          name: `${row.first_name ?? ''} ${row.last_name ?? ''}`.trim(),
        })}
        destructive
        confirmLabel={t('Revoke')}
        onConfirm={onRevoke}
      />
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title={t('Remove patient?')}
        description={t('Remove {{name}} from {{clinic}}?', {
          name: `${row.first_name ?? ''} ${row.last_name ?? ''}`.trim(),
          clinic: row.clinics?.find((c) => c.id === deregisterClinicId)?.name ?? t('this clinic'),
        })}
        destructive
        confirmLabel={t('Remove')}
        onConfirm={onDelete}
      />
      {showMove && (
        <MovePatientClinicDialog
          open={moveOpen}
          onClose={() => setMoveOpen(false)}
          row={row}
          clinicianClinics={clinicianClinics}
          homeHospitalId={homeHospitalId}
        />
      )}
    </div>
  );
};

const SubscriptionBadge = ({ row }: { row: PatientListRow }) => {
  // Hook-safe usage — this is a real component, not a render helper.
  const { t } = useTranslation();
  if (row.has_active_subscription) {
    return <span className="rounded bg-emerald-100 px-2 py-0.5 text-[0.6875rem] font-medium text-emerald-900">{t('Active')}</span>;
  }
  if (row.has_upcoming_subscription) {
    return <span className="rounded bg-sky-100 px-2 py-0.5 text-[0.6875rem] font-medium text-sky-900">{t('Upcoming')}</span>;
  }
  if (row.has_expired_subscription) {
    return <span className="rounded bg-amber-100 px-2 py-0.5 text-[0.6875rem] font-medium text-amber-900">{t('Expired')}</span>;
  }
  if (row.invitation_status === 'pending') {
    return <span className="rounded bg-violet-100 px-2 py-0.5 text-[0.6875rem] font-medium text-violet-900">{t('Pending invite')}</span>;
  }
  return <span className="text-[0.6875rem] text-muted-foreground">—</span>;
};

export default function PatientList() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const setClinician = useAuthStore((s) => s.setClinician);

  const clinicianQuery = useCurrentClinician();
  const clinician = clinicianQuery.data;

  // Mirror what login does — populate the auth store from the clinician fetch.
  useEffect(() => {
    if (!clinician) return;
    if (useAuthStore.getState().clinician) return;
    setClinician({
      id: clinician.id,
      hospital_id: clinician.hospital_id,
      hospital_ids: clinician.hospital_ids,
      first_name: clinician.first_name,
      last_name: clinician.last_name,
      email: clinician.email,
      flags: clinician.flags,
      locale: clinician.locale ?? undefined,
    });
  }, [clinician, setClinician]);

  // ---------- Filter / pagination state synced to URL ----------
  const [searchParams, setSearchParams] = useSearchParams();

  // Retain the filter/pagination view across patient detours — restores the
  // saved query string when the list is opened without one (e.g. via the
  // rail's "All patients" link), persists it otherwise.
  useRetainPatientListFilters(searchParams, setSearchParams);

  const filter = searchParams.get('q') ?? '';
  const status = (searchParams.get('status') as StatusFilter | null) ?? 'all';
  const flag = (searchParams.get('flag') as FlagFilter | null) ?? 'all';
  const addedBy = (searchParams.get('addedBy') as AddedByFilter | null) ?? 'all';
  const clinicIdsParam = searchParams.get('clinics') ?? '';
  const clinicIds = useMemo(
    () =>
      new Set(
        clinicIdsParam
          .split(',')
          .map((s) => Number(s.trim()))
          .filter((n) => Number.isFinite(n) && n > 0),
      ),
    [clinicIdsParam],
  );
  const page = Math.max(1, Number(searchParams.get('page') ?? '1') || 1);
  const sortKey = (searchParams.get('sort') as SortKey | null) ?? 'name';
  const sortDir = (searchParams.get('dir') as SortDir | null) ?? 'asc';
  // Page-size lives in URL so it's bookmarkable. Clamp to a known option;
  // a hand-edited URL with `limit=99999` falls back to the default rather
  // than blowing up the server query.
  const pageSizeRaw = Number(searchParams.get('limit') ?? '');
  const pageSize = (PAGE_SIZE_OPTIONS as readonly number[]).includes(pageSizeRaw)
    ? pageSizeRaw
    : DEFAULT_PAGE_SIZE;

  const updateParams = (updater: (next: URLSearchParams) => void) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        updater(next);
        return next;
      },
      { replace: true },
    );
  };

  // Debounce text input → URL → server query, so each keystroke doesn't fire.
  const [searchInput, setSearchInput] = useState(filter);
  useEffect(() => {
    setSearchInput(filter);
  }, [filter]);
  useEffect(() => {
    if (searchInput === filter) return;
    const handle = setTimeout(() => {
      updateParams((p) => {
        if (searchInput.trim()) p.set('q', searchInput);
        else p.delete('q');
        // Reset pagination whenever the filter changes.
        p.delete('page');
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const setStatus = (next: StatusFilter) => {
    updateParams((p) => {
      if (next === 'all') p.delete('status');
      else p.set('status', next);
      p.delete('page');
    });
    track(ANALYTICS_EVENTS.PATIENT_LIST_FILTER_USED, { filter: 'status', value: next });
  };

  const setFlag = (next: FlagFilter) => {
    updateParams((p) => {
      if (next === 'all') p.delete('flag');
      else p.set('flag', next);
      p.delete('page');
    });
    track(ANALYTICS_EVENTS.PATIENT_LIST_FILTER_USED, { filter: 'flag', value: next });
  };

  const setAddedBy = (next: AddedByFilter) => {
    updateParams((p) => {
      if (next === 'all') p.delete('addedBy');
      else p.set('addedBy', next);
      p.delete('page');
    });
    track(ANALYTICS_EVENTS.PATIENT_LIST_FILTER_USED, { filter: 'addedBy', value: next });
  };

  const setPageSize = (next: number) =>
    updateParams((p) => {
      if (next === DEFAULT_PAGE_SIZE) p.delete('limit');
      else p.set('limit', String(next));
      // Snap back to the first page so the user isn't on a now-out-of-bounds
      // page (e.g. page 7 with 50/page → page 1 with 200/page).
      p.delete('page');
    });

  const onSort = (key: SortKey) =>
    updateParams((p) => {
      if (sortKey === key) {
        p.set('dir', sortDir === 'asc' ? 'desc' : 'asc');
      } else {
        p.set('sort', key);
        p.set('dir', 'asc');
      }
    });

  const setPage = (next: number) =>
    updateParams((p) => {
      if (next <= 1) p.delete('page');
      else p.set('page', String(next));
    });

  const toggleClinic = (id: number) => {
    updateParams((p) => {
      const cur = new Set(clinicIds);
      if (cur.has(id)) cur.delete(id);
      else cur.add(id);
      if (cur.size === 0) p.delete('clinics');
      else p.set('clinics', Array.from(cur).join(','));
      p.delete('page');
    });
    track(ANALYTICS_EVENTS.PATIENT_LIST_FILTER_USED, { filter: 'clinic', value: id });
  };

  // ---------- Queries ----------
  const offset = (page - 1) * pageSize;
  const serverFilter = useMemo(
    () =>
      buildServerFilter({
        search: filter,
        status,
        flag,
        addedBy,
        clinicIds: Array.from(clinicIds),
        clinicianId: clinician?.id,
      }),
    [filter, status, flag, addedBy, clinicIds, clinician?.id],
  );
  const listQuery = usePatientList(clinician?.id, {
    filter: serverFilter,
    limit: pageSize,
    offset: offset > 0 ? String(offset) : undefined,
  });
  const clinics = useClinicianClinics(clinician?.id);
  // hospital_id -> name for the clinic filter's group headers. Built from the
  // clinician's clinics, each of which carries its own hospital_name (FOOD-837),
  // so clinics in EVERY hospital get a correct header. Previously only the home
  // hospital's name was resolved, leaving other hospitals' groups blank.
  const hospitalNameById = useMemo(() => {
    const m: Record<number, string> = {};
    for (const c of clinics.data ?? []) {
      if (c.hospital_id != null && c.hospital_name) m[c.hospital_id] = c.hospital_name;
    }
    return m;
  }, [clinics.data]);
  // clinic id -> hospital id, from the clinician's cross-hospital clinics
  // (FOOD-837). Lets per-patient hospital-scoped actions (resend invite,
  // deregister) target the PATIENT'S hospital instead of the clinician's home
  // one — otherwise they hit the wrong hospital for a patient whose clinic is
  // in a non-home hospital.
  const clinicHospitalById = useMemo(() => {
    const m = new Map<number, number>();
    for (const c of clinics.data ?? []) if (c.hospital_id != null) m.set(c.id, c.hospital_id);
    return m;
  }, [clinics.data]);
  const showClinicFilter = (clinics.data?.length ?? 0) > 1;

  /**
   * Auto rule for which clinics contribute extra columns:
   *   • the clinic's hospital_id matches the clinician's home hospital, OR
   *   • the clinic is explicitly selected in the filter dropdown.
   *
   * The first branch covers normal clinicians (one hospital, all their
   * clinics share its id → all their custom fields show up). The second
   * branch lets internal RxFood clinicians — who are registered across
   * multiple hospitals as a system workaround — light up a foreign
   * clinic's columns on demand by selecting it in the filter, without
   * drowning the table in every other hospital's columns by default.
   *
   * Clinics whose `hospital_id` is missing on the response are treated
   * as matching the home hospital (the existing convention used by
   * `ClinicFilterDropdown`'s `defaultHospitalId` grouping).
   */
  const relevantClinics = useMemo(() => {
    const all = clinics.data ?? [];
    const homeHospitalId = clinicianHomeHospitalId(clinician);
    if (homeHospitalId == null && clinicIds.size === 0) return all;
    return all.filter((c) => {
      const matchesHome =
        homeHospitalId != null
        && (c.hospital_id == null || c.hospital_id === homeHospitalId);
      const selected = clinicIds.has(c.id);
      return matchesHome || selected;
    });
  }, [clinics.data, clinician, clinicIds]);

  const hasExternalIdColumn = useMemo(
    () => anyClinicHasExternalId(relevantClinics),
    [relevantClinics],
  );
  const customFields = useMemo(
    () => unionClinicCustomFields(relevantClinics),
    [relevantClinics],
  );

  // Extra columns slot in after the Email column (see DataTable.columns
  // below). v1 (PatientTable.tsx:114-136) puts External ID first; V2
  // keeps Name first since the layout sorts by name by default. Empty
  // when no relevant clinic declares extras — e.g. an internal user
  // whose home hospital has none and who hasn't selected any foreign
  // clinic in the filter.
  const extraColumns: Column<PatientListRow>[] = useMemo(() => {
    const cols: Column<PatientListRow>[] = [];
    if (hasExternalIdColumn) {
      cols.push({
        key: 'external_id',
        header: t('External ID'),
        cell: (r) => {
          const v = r.hospital_attributes?.patient_external_id;
          return v ? (
            <span className="font-mono text-xs">{v}</span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          );
        },
      });
    }
    for (const f of customFields) {
      cols.push({
        key: `custom_${f.field}`,
        header: f.label,
        cell: (r) => {
          const v = r.hospital_attributes?.patient_custom_fields?.[f.field];
          return v ? (
            <span className="text-xs">{v}</span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          );
        },
      });
    }
    return cols;
  }, [hasExternalIdColumn, customFields, t]);

  // Exact patient-id hoist. When the search box contains a pure number,
  // treat it as a candidate `patient_id` and side-fetch that patient
  // directly. The server's `nameEmail.contains` filter only matches name
  // and email strings, so searching "10" would otherwise miss patient #10
  // unless their name/email happens to contain "10". This hook turns the
  // search box into a working ID lookup without any server changes.
  // Returns 403/404 silently when the clinician doesn't have access to
  // the requested patient — they just won't see the pinned row.
  const numericSearchId = useMemo<number | null>(() => {
    const trimmed = filter.trim();
    if (!/^\d+$/.test(trimmed)) return null;
    const n = Number(trimmed);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [filter]);
  const exactPatient = usePatient(numericSearchId);

  // ---------- Client-side fallback for statuses the server filter can't
  // express, plus client-side sort over the server page. ----------
  const rows = useMemo<PatientListRow[]>(() => {
    const data = listQuery.data?.rows ?? [];

    const needsClientFilter = status === 'no_subscription' || status === 'pending_invitation';
    const filtered = needsClientFilter
      ? data.filter((r) => matchesClientSideStatus(r, status))
      : data;

    const sorted = [...filtered].sort((a, b) => {
      let cmp = 0;
      if (sortKey === 'name') {
        cmp = compareNullable(
          `${a.first_name ?? ''} ${a.last_name ?? ''}`.trim(),
          `${b.first_name ?? ''} ${b.last_name ?? ''}`.trim(),
        );
      } else if (sortKey === 'email') {
        cmp = compareNullable(a.email, b.email);
      } else if (sortKey === 'last_meal') {
        cmp = compareNullable(a.last_meal_date, b.last_meal_date);
      } else if (sortKey === 'logging_period') {
        // Latest logging period start (see the column comment). ISO/YYYY-MM-DD
        // strings compare chronologically via localeCompare; rows with no date
        // fall to the bottom.
        cmp = compareNullable(a.tracking_start_date, b.tracking_start_date);
      } else if (sortKey === 'clinics') {
        // Sort by the first clinic name (the one shown as the cell's primary).
        // Rows with no clinics fall to the bottom via compareNullable.
        cmp = compareNullable(a.clinics?.[0]?.name, b.clinics?.[0]?.name);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });

    // If the search was a numeric id and we resolved an exact patient,
    // hoist it to the top of the list — dedupe against any server result
    // that already includes it so we don't end up with the same row twice.
    if (numericSearchId && exactPatient.data?.patient_id === numericSearchId) {
      const exact = exactPatient.data as PatientListRow;
      return [exact, ...sorted.filter((r) => r.patient_id !== numericSearchId)];
    }

    return sorted;
  }, [listQuery.data, status, sortKey, sortDir, numericSearchId, exactPatient.data]);

  const totalRows = listQuery.data?.total_rows;
  const hasMore = Boolean(listQuery.data?.has_more);

  const [addOpen, setAddOpen] = useState(false);
  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [addMenuOpen, setAddMenuOpen] = useState(false);

  const activeFilterCount =
    (filter ? 1 : 0)
    + (status !== 'all' ? 1 : 0)
    + (flag !== 'all' ? 1 : 0)
    + (addedBy !== 'all' ? 1 : 0)
    + (clinicIds.size > 0 ? 1 : 0);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t('Patients')}</h1>
        {/* Split button: the primary action opens the full enrolment dialog;
            the chevron opens a menu with the lightweight "Quick add to clinic"
            (no logging period, clinic defaults) alongside the full flow. */}
        <div className="flex">
          <Button
            onClick={() => setAddOpen(true)}
            disabled={!clinician}
            className="rounded-r-none"
          >
            <Plus className="mr-2 h-4 w-4" />
            {t('Add patient')}
          </Button>
          <Popover open={addMenuOpen} onOpenChange={setAddMenuOpen}>
            <PopoverTrigger asChild>
              <Button
                disabled={!clinician}
                aria-label={t('More add options')}
                className="rounded-l-none border-l border-primary-foreground/20 px-2"
              >
                <ChevronDown className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-60 p-1">
              <button
                type="button"
                onClick={() => {
                  setAddMenuOpen(false);
                  setAddOpen(true);
                }}
                className="flex w-full flex-col items-start gap-0.5 rounded px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted"
              >
                <span className="font-medium">{t('Add patient (full)')}</span>
                <span className="text-xs text-muted-foreground">
                  {t('Set up a logging period and settings')}
                </span>
              </button>
              <button
                type="button"
                onClick={() => {
                  setAddMenuOpen(false);
                  setQuickAddOpen(true);
                }}
                className="flex w-full flex-col items-start gap-0.5 rounded px-2 py-1.5 text-left text-sm transition-colors hover:bg-muted"
              >
                <span className="font-medium">{t('Quick add to clinic')}</span>
                <span className="text-xs text-muted-foreground">
                  {t('No logging period · clinic defaults')}
                </span>
              </button>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{t('Your patients')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Search + result counts */}
          <div className="flex flex-wrap items-center gap-3">
            <Input
              placeholder={t('Search by name, email, or ID')}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="max-w-sm"
            />
            <div className="ml-auto text-xs text-muted-foreground">
              {totalRows !== undefined ? (
                <>{rows.length} {t('shown')} · {totalRows} {t('total')}</>
              ) : (
                <>{rows.length} {t('shown')}</>
              )}
            </div>
          </div>

          {/* Filters — one dropdown each (each closes on outside click). The
              draft-report option under Report status is gated to internal
              RxFood staff, matching v1. */}
          <div className="flex flex-wrap items-center gap-2">
            {showClinicFilter && (
              <ClinicFilterDropdown
                clinics={clinics.data ?? []}
                hospitalNameById={hospitalNameById}
                // Clinics returned by /hospitals/{h}/clinicians/{c}/clinics often
                // omit `hospital_id`; treat the clinician's hospital_id as the
                // default group key so every clinic lands in the right group.
                defaultHospitalId={clinicianHomeHospitalId(clinician)}
                selectedIds={clinicIds}
                onToggle={toggleClinic}
              />
            )}
            <FilterSelect
              label={t('Patient status')}
              // Combines subscription statuses with the "hasn't logged in a
              // month" flag (mutually exclusive here — one active at a time).
              value={flag === 'not_seen_1mo' ? NOT_LOGGED_VALUE : status}
              options={[
                ...STATUS_OPTIONS.map((s) => ({ value: s as string, label: t(STATUS_LABELS[s]) })),
                { value: NOT_LOGGED_VALUE, label: t(FLAG_LABELS.not_seen_1mo) },
              ]}
              onChange={(v) => {
                if (v === NOT_LOGGED_VALUE) {
                  setStatus('all');
                  setFlag('not_seen_1mo');
                } else {
                  setStatus(v as StatusFilter);
                  if (flag === 'not_seen_1mo') setFlag('all');
                }
              }}
            />
            <FilterSelect
              label={t('Report status')}
              value={flag === 'not_seen_1mo' ? 'all' : flag}
              options={FLAG_OPTIONS.filter(
                (f) => f !== 'not_seen_1mo' && (f !== 'draft_report' || clinician?.is_internal),
              ).map((f) => ({ value: f, label: t(FLAG_LABELS[f]) }))}
              onChange={(v) => {
                // Only touch report flags — don't clobber a 'not_seen_1mo'
                // set via the Patient status dropdown.
                if (v === 'all') {
                  if (flag === 'report_soon' || flag === 'draft_report') setFlag('all');
                } else {
                  setFlag(v);
                }
              }}
            />
            <FilterSelect
              label={t('Added by')}
              value={addedBy}
              options={ADDED_BY_OPTIONS.map((a) => ({ value: a, label: t(ADDED_BY_LABELS[a]) }))}
              onChange={setAddedBy}
            />
            {activeFilterCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  setSearchParams(
                    (prev) => {
                      const next = new URLSearchParams(prev);
                      next.delete('q');
                      next.delete('status');
                      next.delete('flag');
                      next.delete('addedBy');
                      next.delete('clinics');
                      next.delete('page');
                      return next;
                    },
                    { replace: true },
                  )
                }
              >
                <X className="mr-1 h-3.5 w-3.5" />
                {t('Clear filters')}
              </Button>
            )}
          </div>

          {/* Table */}
          <DataTable<PatientListRow>
            isLoading={clinicianQuery.isLoading || listQuery.isLoading}
            rows={rows}
            rowKey={(r) => r.patient_id}
            onRowClick={(r) => navigate(`/patients/${r.patient_id}/summary`)}
            emptyMessage={
              activeFilterCount > 0 ? t('No matching patients.') : t('No patients yet.')
            }
            columns={[
              {
                key: 'patient_id',
                header: t('ID'),
                className: 'w-16',
                cell: (r) => (
                  <span className="tabular-nums text-muted-foreground">{r.patient_id}</span>
                ),
              },
              {
                key: 'name',
                header: <SortHeader label={t('Name')} active={sortKey === 'name'} dir={sortDir} onClick={() => onSort('name')} />,
                cell: (r) => {
                  const name = `${r.first_name ?? ''} ${r.last_name ?? ''}`.trim() || '—';
                  // Render an inline "ID match" tag next to the name when
                  // the row was hoisted via numeric-id lookup. Helps the
                  // clinician spot why a specific patient is pinned at the
                  // top even though a name search wouldn't normally float
                  // them to the top.
                  if (numericSearchId && r.patient_id === numericSearchId) {
                    return (
                      <span className="inline-flex items-center gap-2">
                        <span>{name}</span>
                        <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[0.625rem] font-medium uppercase tracking-wide text-primary">
                          {t('ID match')}
                        </span>
                      </span>
                    );
                  }
                  return name;
                },
              },
              {
                key: 'email',
                header: <SortHeader label={t('Email')} active={sortKey === 'email'} dir={sortDir} onClick={() => onSort('email')} />,
                // Plain text — shown for reference, not a mailto link. Clicking
                // the cell still opens the patient via the row's click handler.
                // Capped + truncated so a long address doesn't stretch the table
                // (full value on hover).
                cell: (r) => (
                  <span className="block max-w-[15rem] truncate" title={r.email ?? undefined}>
                    {r.email ? r.email : '—'}
                  </span>
                ),
              },
              // Which clinic(s) the patient belongs to. Gated on the same
              // >1-clinic condition as the clinic filter: for a single-clinic
              // clinician every row is the same clinic, so the column would just
              // repeat one name and eat width for nothing. Kept compact — first
              // clinic + "+N", with the full list on hover.
              ...(showClinicFilter
                ? ([
                    {
                      key: 'clinics',
                      // Secondary column — hidden on narrower viewports so the
                      // table doesn't need horizontal scrolling; returns at xl.
                      className: 'hidden xl:table-cell',
                      header: (
                        <SortHeader
                          label={t('Clinics')}
                          active={sortKey === 'clinics'}
                          dir={sortDir}
                          onClick={() => onSort('clinics')}
                        />
                      ),
                      cell: (r: PatientListRow) => {
                        const cs = r.clinics ?? [];
                        const first = cs[0];
                        if (!first) return <span className="text-muted-foreground">—</span>;
                        const all = cs.map((c) => c.name).join(', ');
                        return (
                          <span
                            className="block max-w-[9rem] truncate text-xs text-muted-foreground"
                            title={all}
                          >
                            {cs.length === 1 ? first.name : `${first.name} +${cs.length - 1}`}
                          </span>
                        );
                      },
                    },
                  ] as Column<PatientListRow>[])
                : []),
              // Clinic-defined extra columns (External ID + custom fields)
              // slot in after the patient's email. Spread so the column list
              // stays a single flat array. Empty when the toggle is off or
              // no clinic in the clinician's scope declares any extras.
              ...extraColumns,
              {
                key: 'phone',
                className: 'hidden xl:table-cell',
                header: t('Phone'),
                cell: (r) =>
                  r.phone_number ? (
                    <a
                      href={`tel:${r.phone_number}`}
                      onClick={(e) => e.stopPropagation()}
                      className="tabular-nums text-foreground hover:text-primary hover:underline"
                    >
                      {r.phone_number}
                    </a>
                  ) : (
                    '—'
                  ),
              },
              // Latest logging period — backed by `tracking_start_date` (the
              // patient's current tracking-period start). INTERIM CHOICE: we
              // avoided `most_recent_subscription_start_date` because it's the
              // MAX subscription start, so a FUTURE-scheduled logging period
              // would show a future date instead of the current active one.
              // `tracking_start_date` skips that trap but can go stale (it isn't
              // always the very latest period). The proper fix is a server field
              // for "latest already-started logging period" (+ orderable for
              // cross-page sort) — tracked as an MNT ticket. Sort here is
              // page-local like the other columns until that lands.
              {
                key: 'logging_period',
                header: (
                  <SortHeader
                    label={t('Latest Logging Period')}
                    active={sortKey === 'logging_period'}
                    dir={sortDir}
                    onClick={() => onSort('logging_period')}
                  />
                ),
                cell: (r) => (
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {formatDateShort(r.tracking_start_date)}
                  </span>
                ),
              },
              {
                key: 'next_report',
                className: 'hidden 2xl:table-cell',
                header: t('Next report'),
                cell: (r) => (
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {formatDateShort(r.next_report_date)}
                  </span>
                ),
              },
              {
                key: 'last_report',
                className: 'hidden 2xl:table-cell',
                header: t('Last report'),
                cell: (r) => (
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {formatDateShort(r.last_completed_report_date)}
                  </span>
                ),
              },
              {
                key: 'last_meal',
                className: 'hidden lg:table-cell',
                header: <SortHeader label={t('Last meal')} active={sortKey === 'last_meal'} dir={sortDir} onClick={() => onSort('last_meal')} />,
                cell: (r) => (
                  <span className="text-xs tabular-nums text-muted-foreground">
                    {formatDateShort(r.last_meal_date)}
                  </span>
                ),
              },
              {
                key: 'status',
                header: t('Status'),
                cell: (r) => <SubscriptionBadge row={r} />,
              },
              {
                key: 'flags',
                header: <span className="sr-only">{t('Actions')}</span>,
                className: 'w-40 text-right',
                cell: (r) => (
                  <PatientFlagIcons
                    row={r}
                    // The patient's own clinic's hospital (resolved from the
                    // clinician's cross-hospital clinics), NOT the clinician's
                    // home hospital — so resend/deregister hit the right
                    // hospital for a patient in a non-home hospital. Falls back
                    // to the home hospital when the clinic can't be resolved.
                    hospitalId={
                      clinicHospitalById.get(r.clinics?.[0]?.id ?? -1) ??
                      clinicianHomeHospitalId(clinician)
                    }
                    // First clinic the row is registered in — used as the
                    // deregister target. v1 picks the first clinic the
                    // current clinician shares with the patient; we default
                    // to the row's first clinic since the server already
                    // scopes the list to clinics the clinician can see.
                    defaultClinicId={r.clinics?.[0]?.id}
                    clinicianClinics={clinics.data ?? []}
                    homeHospitalId={clinicianHomeHospitalId(clinician)}
                  />
                ),
              },
            ]}
          />

          {/* Pagination — always rendered (even when there's only one
              page) so the page-size dropdown stays accessible. Previously
              the whole row was hidden on small result sets, but that
              made the limit selector unreachable. */}
          <Pagination
            page={page}
            totalRows={totalRows}
            pageSize={pageSize}
            pageSizeOptions={PAGE_SIZE_OPTIONS}
            hasMore={hasMore}
            isFetching={listQuery.isFetching}
            onChange={setPage}
            onPageSizeChange={setPageSize}
          />
        </CardContent>
      </Card>

      {clinician && (
        <>
          <NewPatientDialog
            open={addOpen}
            clinicianId={clinician.id}
            onClose={() => setAddOpen(false)}
            onCreated={(patientId) => navigate(`/patients/${patientId}/summary`)}
          />
          <QuickAddPatientDialog
            open={quickAddOpen}
            clinicianId={clinician.id}
            onClose={() => setQuickAddOpen(false)}
            onCreated={(patientId) => navigate(`/patients/${patientId}/summary`)}
          />
        </>
      )}
    </div>
  );
}

/**
 * Searchable, hospital-grouped clinic filter dropdown. Selected clinics
 * float to a pinned "Selected" pseudo-group at the top of the list so
 * the user can see what's active without scrolling — useful when there
 * are many clinics. The unselected remainder is grouped by hospital;
 * cross-hospital users get hospital headings, single-hospital users get
 * a single unlabelled group.
 *
 * The text search filters on clinic.name (case-insensitive), and also on the
 * clinic id for internal users (who can see ids via ClinicLabel). Empty
 * groups after filtering are hidden.
 */
const ClinicFilterDropdown = ({
  clinics,
  hospitalNameById,
  defaultHospitalId,
  selectedIds,
  onToggle,
}: {
  clinics: Array<{ id: number; name: string; hospital_id?: number }>;
  hospitalNameById: Record<number, string>;
  /** Group key fallback when a clinic row lacks `hospital_id`. */
  defaultHospitalId?: number;
  selectedIds: Set<number>;
  onToggle: (id: number) => void;
}) => {
  const { t } = useTranslation();
  const isInternal = useIsInternalUser();
  const [query, setQuery] = useState('');
  // Internal users can see clinic ids (via ClinicLabel), so let them search
  // by id as well as name. Regular clinicians match on name only.
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return clinics;
    return clinics.filter(
      (c) => c.name.toLowerCase().includes(q) || (isInternal && String(c.id).includes(q)),
    );
  }, [clinics, query, isInternal]);

  /**
   * Group layout:
   *   1. "Selected" pseudo-group at the top — every selected clinic,
   *      regardless of hospital. Skipped when nothing is selected.
   *   2. Remaining unselected clinics, grouped by hospital_id. Each
   *      hospital gets a heading (or none if there's only one group).
   * Selected clinics are NOT duplicated under their hospital — once
   * selected, they live in the top group until deselected.
   */
  type Group = {
    /** Stable key. Numeric for hospital groups; the literal 'selected'
     *  for the pinned pseudo-group. */
    key: string;
    /** Heading shown above the group; empty hides the heading. */
    label: string;
    items: typeof clinics;
  };

  const groups = useMemo<Group[]>(() => {
    const selected: typeof clinics = [];
    const unselected: typeof clinics = [];
    for (const c of filtered) {
      if (selectedIds.has(c.id)) selected.push(c);
      else unselected.push(c);
    }

    const byHospital = new Map<number | null, typeof clinics>();
    for (const c of unselected) {
      const key = c.hospital_id ?? defaultHospitalId ?? null;
      const list = byHospital.get(key) ?? [];
      list.push(c);
      byHospital.set(key, list);
    }

    const result: Group[] = [];
    if (selected.length > 0) {
      result.push({ key: 'selected', label: t('Selected'), items: selected });
    }
    for (const [hospitalId, items] of byHospital.entries()) {
      result.push({
        key: String(hospitalId),
        label: hospitalId !== null ? (hospitalNameById[hospitalId] ?? '') : '',
        items,
      });
    }
    return result;
  }, [filtered, selectedIds, hospitalNameById, defaultHospitalId, t]);

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn('h-9 gap-1.5', selectedIds.size > 0 && 'border-primary text-primary')}
        >
          <span className={cn('text-xs', selectedIds.size > 0 ? 'text-primary/80' : 'text-muted-foreground')}>
            {t('Clinic')}
          </span>
          {selectedIds.size > 0 && (
            <span className="rounded bg-primary/10 px-1.5 text-xs font-medium text-primary">
              {selectedIds.size}
            </span>
          )}
          <ChevronDown className="h-3.5 w-3.5 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-72 p-0">
        <div className="border-b p-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              type="text"
              autoFocus
              placeholder={isInternal ? t('Search by name or ID') : t('Search clinics')}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="h-8 w-full rounded-md border border-input bg-background pl-7 pr-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto p-1">
          {groups.length === 0 ? (
            <p className="px-3 py-4 text-center text-xs text-muted-foreground">
              {t('No matches')}
            </p>
          ) : (
            groups.map((group) => {
              // Tidy: a hospital with a single clinic collapses to one row
              // labelled with the hospital name — no redundant header above a
              // lone clinic. The "Selected" pseudo-group and hospitals without a
              // resolved name keep the header + clinic-name rows.
              const singleClinicHospital =
                group.key !== 'selected' && group.items.length === 1 && !!group.label;
              return (
                <div key={group.key} className="mb-1 last:mb-0">
                  {group.label && !singleClinicHospital && (
                    <div className="px-2 pt-2 pb-1 text-[0.6875rem] font-semibold uppercase tracking-wide text-muted-foreground">
                      {group.label}
                    </div>
                  )}
                  {group.items.map((c) => {
                    const checked = selectedIds.has(c.id);
                    return (
                      <label
                        key={c.id}
                        className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm hover:bg-muted"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => onToggle(c.id)}
                          className="rounded border-input"
                        />
                        {singleClinicHospital ? (
                          <span className="truncate">{group.label}</span>
                        ) : (
                          <ClinicLabel name={c.name} id={c.id} className="truncate" />
                        )}
                      </label>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};

/**
 * Patient-list pagination control. Shows Prev / Next plus "Page X of Y"
 * and a numeric jump-to-page input.
 *
 * The total page count is computed from `total_rows` on the patients-table
 * response (server-side count, populated by the listing endpoint). When the
 * server omits `total_rows`, we fall back to a count-less "Page X" with
 * Prev/Next only — same UX as the previous implementation.
 *
 * The jump input is an uncontrolled-ish field: local state holds the
 * draft value so the clinician can edit freely without each keystroke
 * triggering a query. The page is only changed on Enter or blur, and we
 * clamp + ignore invalid input rather than show inline validation errors
 * (the worst case is "nothing happened" which is fine for a tiny widget).
 */
const Pagination = ({
  page,
  totalRows,
  pageSize,
  pageSizeOptions,
  hasMore,
  isFetching,
  onChange,
  onPageSizeChange,
}: {
  page: number;
  totalRows: number | undefined;
  pageSize: number;
  pageSizeOptions: readonly number[];
  hasMore: boolean;
  isFetching: boolean;
  onChange: (next: number) => void;
  onPageSizeChange: (next: number) => void;
}) => {
  const { t } = useTranslation();
  const totalPages = totalRows != null && totalRows > 0
    ? Math.max(1, Math.ceil(totalRows / pageSize))
    : null;
  const [draft, setDraft] = useState(String(page));

  // Keep the input in sync when the page changes from outside (Prev/Next
  // clicks, status-filter changes that reset to page 1, etc.).
  useEffect(() => {
    setDraft(String(page));
  }, [page]);

  const commit = () => {
    const n = Number(draft);
    // Reject anything non-numeric or out of range; reset the draft to the
    // current page so the input doesn't show a stale invalid value.
    if (!Number.isFinite(n) || n < 1) {
      setDraft(String(page));
      return;
    }
    const clamped = totalPages != null ? Math.min(totalPages, Math.max(1, Math.floor(n))) : Math.max(1, Math.floor(n));
    if (clamped === page) {
      setDraft(String(page));
      return;
    }
    onChange(clamped);
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 border-t pt-3">
      <Button
        variant="outline"
        size="sm"
        disabled={page === 1 || isFetching}
        onClick={() => onChange(page - 1)}
      >
        {t('Previous')}
      </Button>

      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <label htmlFor="patient-page-input" className="whitespace-nowrap">
          {t('Page')}
        </label>
        <input
          id="patient-page-input"
          type="number"
          min={1}
          max={totalPages ?? undefined}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={commit}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit();
              (e.target as HTMLInputElement).blur();
            }
          }}
          disabled={isFetching}
          className="h-7 w-14 rounded-md border border-input bg-background px-1.5 text-center text-xs tabular-nums focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
          aria-label={t('Page number')}
        />
        {totalPages != null && (
          <span className="whitespace-nowrap">{t('of {{n}}', { n: totalPages })}</span>
        )}
      </div>

      <div className="flex items-center gap-2">
        {/* Page-size dropdown — replaces v1's "Limit" filter. Sits next
            to Next so the Prev/Next pair stays together visually and the
            page-size feels like a meta-control rather than a chip. */}
        <label className="flex items-center gap-1 text-xs text-muted-foreground">
          <span className="hidden sm:inline">{t('Per page')}</span>
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            disabled={isFetching}
            className="h-7 rounded-md border border-input bg-background px-1.5 text-xs focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label={t('Rows per page')}
          >
            {pageSizeOptions.map((n) => (
              <option key={n} value={n}>{n}</option>
            ))}
          </select>
        </label>
        <Button
          variant="outline"
          size="sm"
          disabled={!hasMore || isFetching}
          onClick={() => onChange(page + 1)}
        >
          {t('Next')}
        </Button>
      </div>
    </div>
  );
};
